import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { startViewTransition } from '@theme/utilities';
import { hasDisplayedPriceControls, normalizeDisplayedPriceResults, rememberRenderedFacetState } from './facets.js';

/**
 * AJAX-enabled pagination component
 * Handles pagination clicks and updates the section without full page reload
 */
export default class Pagination extends Component {
  connectedCallback() {
    super.connectedCallback();
    
    // Add click event listeners to all pagination links
    this.addEventListener('click', this.handlePaginationClick.bind(this));
  }

  /**
   * Handles pagination link clicks
   * @param {Event} event - The click event
   */
  async handlePaginationClick(event) {
    if (!event.target || !(event.target instanceof HTMLElement)) return;
    
    const target = event.target.closest('a.pagination__arrow, a.pagination__number');
    
    if (!target || !(target instanceof HTMLAnchorElement)) return;
    
    event.preventDefault();
    
    const url = new URL(target.href);
    url.hash = '';
    const resultsListElement = this.closest('results-list');
    
    if (!resultsListElement) {
      // Fallback to regular navigation if results-list is not found
      window.location.href = target.href;
      return;
    }
    
    const sectionId = resultsListElement.getAttribute('section-id');
    
    if (!sectionId) {
      // Fallback to regular navigation if section ID is not found
      window.location.href = target.href;
      return;
    }

    // Price sort/filter renders the full result set client-side on page 1.
    // Ignore native pagination and re-normalize instead of loading a single server page.
    if (hasDisplayedPriceControls(new URL(window.location.href))) {
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('page');
      history.replaceState({}, '', cleanUrl.toString());
      rememberRenderedFacetState(cleanUrl);

      this.disablePaginationLinks();

      try {
        await startViewTransition(
          () => normalizeDisplayedPriceResults(sectionId),
          ['product-grid'],
        );

        const resultsElement = document.getElementById('ResultsList');
        if (resultsElement) {
          resultsElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      } catch (error) {
        console.error('Displayed-price pagination normalization failed:', error);
      } finally {
        this.enablePaginationLinks();
      }
      return;
    }

    // Disable all pagination links to prevent double-clicks
    this.disablePaginationLinks();

    try {
      // Update URL without page reload
      history.pushState({}, '', url.toString());
      
      // Use view transition for smooth animation
      await startViewTransition(
        () => sectionRenderer.renderSection(sectionId, { cache: false }),
        ['product-grid']
      );
      rememberRenderedFacetState(url);
      
      // Scroll to the top of results
      const resultsElement = document.getElementById('ResultsList');
      if (resultsElement) {
        resultsElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start'
        });
      }
    } catch (error) {
      console.error('Pagination update failed:', error);
      // Fallback to regular navigation on error
      window.location.href = target.href;
    } finally {
      // Re-enable pagination links after update
      this.enablePaginationLinks();
    }
  }

  /**
   * Disables all pagination links
   */
  disablePaginationLinks() {
    const links = this.querySelectorAll('a.pagination__arrow, a.pagination__number');
    links.forEach(link => {
      if (link instanceof HTMLElement) {
        link.style.pointerEvents = 'none';
        link.style.opacity = '0.5';
      }
    });
  }

  /**
   * Enables all pagination links
   */
  enablePaginationLinks() {
    const links = this.querySelectorAll('a.pagination__arrow, a.pagination__number');
    links.forEach(link => {
      if (link instanceof HTMLElement) {
        link.style.pointerEvents = '';
        link.style.opacity = '';
      }
    });
  }
}

if (!customElements.get('pagination-controls')) {
  customElements.define('pagination-controls', Pagination);
}

